import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import Layout from "../components/Layout"
import ShareButtons from "../components/ShareLinks"
import { Seo } from "../components/Seo"
import { useLocation } from "@reach/router"
import { PortableText } from "@portabletext/react"
import urlBuilder from "@sanity/image-url"
import ExploreLink from "../components/ExploreLink"
import useDeviceCheck from "../deviceCheck"

const ComponentName = ({
  data: {
    updates: {
      upTitle,
      upImage,
      upSlug,
      upDescription,
      metaDescription,
      categoryTag,
      image,
      _rawContent: json,
    },
    allSanityUpdates,
  },
}) => {
  const cnt = json
  const pathLocation = useLocation()

  const isTablet = useDeviceCheck("tablet")
  const isMobile = useDeviceCheck("mobile")
  const url = pathLocation.href

  const SampleImageComponent = ({ value, isInline }) => {
    return (
      <img
        src={urlBuilder({ projectId: "3hfxs7a8", dataset: "production" })
          .image(value)
          .url()}
        alt={upImage.asset.altText || "Update Image banner"}
        className="update-cnt-image my-3"
        loading="lazy"
        style={{
          display: isInline ? "inline-block" : "block",
        }}
      />
    )
  }

  const myPortableTextComponents = {
    block: {
      normal: ({ children }) => <p className="up-paragraph">{children}</p>,
      h1: ({ children }) => <h1 className="heading1">{children}</h1>,
      h2: ({ children }) => <h2 className="heading2">{children}</h2>,
      h3: ({ children }) => <h3 className="heading3">{children}</h3>,
      h4: ({ children }) => <h4 className="heading4">{children}</h4>,
      h5: ({ children }) => <h5 className="heading5">{children}</h5>,
      h6: ({ children }) => <h6 className="heading6">{children}</h6>,
      blockquote: ({ children }) => (
        <blockquote className="custom-blockquote">{children}</blockquote>
      ),
    },

    types: {
      image: SampleImageComponent,
    },

    list: {
      bullet: ({ children }) => {
        return <ul className="ul-list">{children}</ul>
      },
      number: ({ children }) => <ol className="ol-list">{children}</ol>,
      checkmarks: ({ children }) => (
        <ol className="m-auto text-lg">{children}</ol>
      ),
    },

    listItem: {
      bullet: ({ children, value: { style } }) => (
        <li>
          {style === "normal" ? (
            <p className="up-paragraph">{children}</p>
          ) : (
            children
          )}
        </li>
      ),
    },

    marks: {
      link: ({ children, value }) => {
        const rel = !value?.href?.startsWith("/")
          ? "noreferrer noopener"
          : undefined
        return (
          <a href={value.href} rel={rel}>
            {children}
          </a>
        )
      },
    },
  }

  const [nextUpdate, setNextUpdate] = useState(null)

  useEffect(() => {
    const filteredUpdates = allSanityUpdates.nodes.filter(
      update => update.upSlug !== upSlug
    )
    if (filteredUpdates.length > 0) {
      const randomUpdate =
        filteredUpdates[Math.floor(Math.random() * filteredUpdates.length)]
      setNextUpdate(randomUpdate)
    }
  }, [allSanityUpdates, upSlug])

  return (
    <div className="single-sog-up">
      <Layout hideFooter={true}>
        <Seo
          title={metaDescription}
          description={upDescription[0].children[0].text}
          image={image?.asset?.url ?? upImage.asset.url}
          url={url}
        />

        <div className="up-container single-update-container">
          <div className="inner-update-container">
            {isTablet && (
              <div>
                {categoryTag && categoryTag.length > 0 && (
                  <span className="d-flex mobile-category-tag">
                    {categoryTag.slice(0, 2).map(tag => (
                      <span
                        key={tag._id}
                        className="category-tag subtitle pb-0 mt-0"
                      >
                        {tag.tag}
                      </span>
                    ))}
                  </span>
                )}
                <h1 className="title pt-2 mb-4 justify-content-start text-left h2-styling">
                  {upTitle}
                </h1>
              </div>
            )}
            <div className="d-flex main-content-container justify-content-center">
              <div className="updates-images">
                <div
                  className="cs-logo sticky-image mb-3"
                  style={{ backgroundImage: `url(${upImage.asset.url})` }}
                ></div>
                {nextUpdate && !isMobile && !isTablet && (
                  <div
                    className="next-update-card shadowed-card d-flex align-items-end py-2 px-3"
                    style={{
                      backgroundImage: `url(${nextUpdate.upImage.asset.url})`,
                    }}
                  >
                    <div className="explore-link-div">
                      <ExploreLink
                        text="Next Update"
                        to={`/updates/${nextUpdate.upSlug}`}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="single-up-content scrollable-text">
                {!isTablet && (
                  <div>
                    {categoryTag && categoryTag.length > 0 && (
                      <span className="d-flex">
                        {categoryTag.slice(0, 2).map(tag => (
                          <span
                            key={tag._id}
                            className="category-tag subtitle pb-0 mt-0"
                          >
                            {tag.tag}
                          </span>
                        ))}
                      </span>
                    )}
                    <h1 className="title pt-2 mb-4 justify-content-start h2-styling">
                      {upTitle}
                    </h1>
                  </div>
                )}
                <div className="single-update">
                  <PortableText
                    value={cnt}
                    components={myPortableTextComponents}
                  />
                </div>
                <div className={`share-article d-flex ${isTablet ? "margin-bottom-2rem" : ""}`}>
                  <ShareButtons
                    url={url ? url : " "}
                    title={upTitle}
                    summary={upTitle}
                    body={`${upTitle}\n\n Visit: ${url}\n`}
                  />
                </div>
              </div>
              {nextUpdate && (isMobile || isTablet) && (
                <div
                  className="next-update-card shadowed-card d-flex align-items-end py-2 px-3"
                  style={{
                    backgroundImage: `url(${nextUpdate.upImage.asset.url})`,
                  }}
                >
                  <div className="explore-link-div">
                    <ExploreLink
                      text="Next Update"
                      to={`/updates/${nextUpdate.upSlug}`}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    </div>
  )
}

export const query = graphql`
  query GetUpdatesAndSingleUpdate($slug: String) {
    allSanityUpdates(sort: { order: DESC, fields: upDate }) {
      nodes {
        id
        upDate(formatString: "DD MMMM YYYY")
        upSlug
        upTitle
        upImage {
          asset {
            url
            altText
          }
        }
        categoryTag {
          tag
        }
      }
    }

    updates: sanityUpdates(upSlug: { eq: $slug }) {
      upTitle
      upSlug
      upDescription {
        children {
          text
        }
      }
      categoryTag {
        _id
        tag
      }
      upImage {
        asset {
          url
          altText
        }
      }
      upDate(formatString: "MMM. DD, YYYY, h:mm a")
      _rawContent
      metaDescription
      image {
        asset {
          url
        }
      }
    }
  }
`

export default ComponentName
